import React from 'react';
import { DollarSign, CheckCircle, XCircle, Clock, Edit2, Trash2, FileText, Clock as ClockIcon } from 'lucide-react';
import { format } from 'date-fns';
import { calculateDaysOld } from '../../lib/format';

export const ExpenseTable = ({ expenses, currentUser, onEdit, onDelete, onAddNote, onViewAudit, onApprove }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
    <div className="overflow-x-auto">
      <table className="w-full" data-testid="expenses-table">
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr>
            <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">Expense ID</th>
            <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">Date</th>
            <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">Vendor</th>
            <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">Category</th>
            <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">Description</th>
            <th className="text-right py-4 px-6 text-sm font-semibold text-gray-700">Amount</th>
            <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">Entered By</th>
            <th className="text-left py-4 px-6 text-sm font-semibold text-gray-700">Status</th>
            <th className="text-center py-4 px-6 text-sm font-semibold text-gray-700">Actions</th>
          </tr>
        </thead>
        <tbody>
          {expenses.map((expense) => {
            const daysOld = calculateDaysOld(expense.created_at);
            const canEdit = (daysOld <= 10 && !expense.is_locked) || currentUser?.role === 'Admin';
            return (
              <tr key={expense.id} className="border-b border-gray-100 hover:bg-gray-50 transition">
                <td className="py-4 px-6">
                  <div className="flex items-center space-x-2">
                    <DollarSign className="w-4 h-4 text-green-600" />
                    <span className="font-semibold text-gray-800">{expense.expense_id}</span>
                  </div>
                </td>
                <td className="py-4 px-6 text-sm text-gray-600">
                  {format(new Date(expense.date), 'MMM dd, yyyy')}
                </td>
                <td className="py-4 px-6 text-sm text-gray-600">{expense.vendor_payee}</td>
                <td className="py-4 px-6">
                  <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-700">
                    {expense.category}
                  </span>
                </td>
                <td className="py-4 px-6 text-sm text-gray-600 max-w-xs truncate">{expense.description}</td>
                <td className="py-4 px-6 text-sm font-bold text-gray-800 text-right">₹{expense.amount.toFixed(2)}</td>
                <td className="py-4 px-6 text-sm text-gray-600">{expense.entered_by}</td>
                <td className="py-4 px-6">
                  <span className={`inline-flex items-center space-x-1 px-2 py-1 text-xs font-semibold rounded-full ${
                    expense.status === 'Pending' ? 'bg-yellow-100 text-yellow-700' :
                    expense.status === 'Approved' ? 'bg-green-100 text-green-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {expense.status === 'Pending' && <Clock className="w-3 h-3" />}
                    {expense.status === 'Approved' && <CheckCircle className="w-3 h-3" />}
                    {expense.status === 'Rejected' && <XCircle className="w-3 h-3" />}
                    <span>{expense.status}</span>
                  </span>
                </td>
                <td className="py-4 px-6">
                  <div className="flex items-center justify-center space-x-2">
                    {canEdit ? (
                      <>
                        <button onClick={() => onEdit(expense)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition" title="Edit" data-testid={`edit-expense-${expense.expense_id}`}>
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => onDelete(expense)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition" title="Delete" data-testid={`delete-expense-${expense.expense_id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    ) : (
                      <span className="text-xs text-gray-400 px-2">Locked</span>
                    )}
                    <button onClick={() => onAddNote(expense)} className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition" title="Add Note" data-testid={`note-expense-${expense.expense_id}`}>
                      <FileText className="w-4 h-4" />
                    </button>
                    {expense.audit_logs && expense.audit_logs.length > 0 && (
                      <button onClick={() => onViewAudit(expense)} className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition" title="View Audit Log" data-testid={`audit-expense-${expense.expense_id}`}>
                        <ClockIcon className="w-4 h-4" />
                      </button>
                    )}
                    {currentUser?.role === 'Admin' && expense.status === 'Pending' && (
                      <>
                        <button onClick={() => onApprove(expense.expense_id, true)} className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition" title="Approve" data-testid={`approve-expense-${expense.expense_id}`}>
                          <CheckCircle className="w-4 h-4" />
                        </button>
                        <button onClick={() => onApprove(expense.expense_id, false)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition" title="Reject" data-testid={`reject-expense-${expense.expense_id}`}>
                          <XCircle className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </div>
);
